/**
 * Group tactics (Phase 66.4, decision 119; the design note in BUILDLIST Phase 66): fire and manoeuvre as
 * **tasks** over the two-tier orders. A tactic splits the squad by its formation order and gives each half
 * a part; it ends when it is done or expires. Two tactics:
 *   - `bound` (bounding overwatch): the squad advances on a point in bounds — one half holds and faces the
 *     point (overwatch) while the other walks toward it; every `BOUND_SECONDS` the halves swap;
 *   - `flank`: the front half holds facing the point while the rear half walks to a flank point, off to
 *     one side of the objective, so the attack comes from two directions.
 * Pure; deterministic math only. The action bot carries each member's part out live, as it does a slot.
 */
import type { GameState, MatchSquad, PlayerState, SquadOrder, Vec3 } from '../types';
import { vecAdd, vecCross, vecDot, vecLength, vecNormalize, vecScale, vecSub } from '../math';
import { dacos, dcos, dsin } from './dmath';
import { PHYS_DT } from '../constants';
import { formationOrder } from './formation';
import { orderPlace } from './orderForms';

export type TacticOrder = Extract<SquadOrder, { kind: 'bound' | 'flank' }>;
export type FormationSquad = Pick<MatchSquad, 'unitIds' | 'formation'>;

/** A bound: how long one half moves before the halves swap. */
export const BOUND_SECONDS = 6;
/** The flank point sits this far from the objective, across the squad's line of approach (person lengths). */
export const FLANK_OFFSET = 8;
/** A flanker within this of the flank point is in position (person lengths). */
export const FLANK_ARRIVE = 2;

export const isTactic = (o: SquadOrder | null | undefined): o is TacticOrder => !!o && (o.kind === 'bound' || o.kind === 'flank');

/** The squad's two halves by formation order: the leader's half (even slots) and the other (odd slots). */
export function tacticHalves(state: Pick<GameState, 'players'>, squad: FormationSquad): { even: PlayerState[]; odd: PlayerState[] } {
  const order = formationOrder(state, squad);
  return { even: order.filter((_, i) => i % 2 === 0), odd: order.filter((_, i) => i % 2 === 1) };
}

/** The squad's front and rear halves by formation order (the rear takes the larger half when odd). */
export function tacticFrontRear(state: Pick<GameState, 'players'>, squad: FormationSquad): { front: PlayerState[]; rear: PlayerState[] } {
  const order = formationOrder(state, squad);
  const n = Math.floor(order.length / 2);
  return { front: order.slice(0, n), rear: order.slice(n) };
}

/** Which bound this is (0, 1, 2 …) since the task was issued; even bounds move the odd half first. */
export function boundPhase(issuedTick: number, tick: number): number {
  return Math.floor(Math.max(0, tick - issuedTick) / Math.round(BOUND_SECONDS / PHYS_DT));
}

/** Walk a surface direction toward another by `arc` world units on a planet of `radius` (no overshoot). */
export function stepToward(from: Vec3, to: Vec3, arc: number, radius: number): Vec3 {
  const a = vecNormalize(from), b = vecNormalize(to);
  const cosT = Math.max(-1, Math.min(1, vecDot(a, b)));
  const total = dacos(cosT) * radius;
  if (total <= arc || total < 1e-9) return b;
  const t = vecSub(b, vecScale(a, cosT));
  const len = vecLength(t);
  if (len < 1e-12) return b;
  const theta = arc / radius;
  return vecNormalize(vecAdd(vecScale(a, dcos(theta)), vecScale(vecScale(t, 1 / len), dsin(theta))));
}

/** The flank point: `FLANK_OFFSET` person lengths from the objective, across the squad's line of approach, to `side`. */
export function flankPoint(state: Pick<GameState, 'players' | 'planets'>, squad: FormationSquad, site: Vec3, planetIndex: number, side: 'left' | 'right', personLength: number): Vec3 | null {
  const order = formationOrder(state, squad);
  const lead = order[0];
  if (!lead || lead.planetIndex !== planetIndex) return null;
  const n = vecNormalize(site);
  // the direction from the objective toward the squad, in the objective's ground plane
  const toward = vecSub(vecNormalize(lead.surfaceDir), vecScale(n, vecDot(vecNormalize(lead.surfaceDir), n)));
  if (vecLength(toward) < 1e-9) return null;
  const t = vecNormalize(toward);
  const across = side === 'left' ? vecNormalize(vecCross(n, t)) : vecNormalize(vecCross(t, n));
  const radius = state.planets[planetIndex].radius;
  const theta = (FLANK_OFFSET * personLength) / radius;
  return vecNormalize(vecAdd(vecScale(n, dcos(theta)), vecScale(across, dsin(theta))));
}

export interface TacticPart {
  /** Walk to this surface direction (null: hold where you are). */
  dest: Vec3 | null;
  role: 'move' | 'hold';
  /** Face this surface direction (the objective). */
  face: Vec3;
  planetIndex: number;
}

/**
 * A member's part in the squad's tactic right now. Null when the tactic cannot run (no such point, the
 * member on another planet, not in the squad).
 */
export function tacticPart(state: Pick<GameState, 'players' | 'planets' | 'modeState'>, squad: FormationSquad, unitId: number, order: TacticOrder, issuedTick: number, tick: number, personLength: number): TacticPart | null {
  const site = orderPlace(state, order);   // 68.0: a point, or a place
  if (!site) return null;
  const me = state.players.find(p => p.id === unitId);
  if (!me || !me.alive || me.planetIndex !== site.planetIndex) return null;
  if (order.kind === 'bound') {
    const { even, odd } = tacticHalves(state, squad);
    const oddMoves = boundPhase(issuedTick, tick) % 2 === 0;
    const mine = odd.some(p => p.id === unitId) ? 'odd' : even.some(p => p.id === unitId) ? 'even' : null;
    if (!mine) return null;
    // a lone half (one living member) walks every bound: nobody is left to cover
    const moving = (mine === 'odd') === oddMoves || (mine === 'odd' ? even : odd).length === 0;
    return { dest: moving ? site.surfaceDir : null, role: moving ? 'move' : 'hold', face: site.surfaceDir, planetIndex: site.planetIndex };
  }
  const { front, rear } = tacticFrontRear(state, squad);
  const flanker = rear.some(p => p.id === unitId);
  if (!flanker && !front.some(p => p.id === unitId)) return null;
  if (!flanker) return { dest: null, role: 'hold', face: site.surfaceDir, planetIndex: site.planetIndex };
  const fp = flankPoint(state, squad, site.surfaceDir, site.planetIndex, order.side, personLength);
  return { dest: fp ?? site.surfaceDir, role: 'move', face: site.surfaceDir, planetIndex: site.planetIndex };
}

/** Whether the tactic has done its work: a bound when a member stands on the point; a flank when a flanker is in position. */
export function tacticComplete(state: Pick<GameState, 'players' | 'planets' | 'modeState'>, squad: FormationSquad, order: TacticOrder, personLength: number, arriveArc: number): boolean {
  const site = orderPlace(state, order);   // 68.0: a point, or a place
  if (!site) return false;
  const radius = state.planets[site.planetIndex].radius;
  const arc = (a: Vec3, b: Vec3) => dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(a), vecNormalize(b))))) * radius;
  const alive = formationOrder(state, squad).filter(p => p.planetIndex === site.planetIndex);
  if (order.kind === 'bound') return alive.some(p => arc(p.surfaceDir, site.surfaceDir) < arriveArc);
  const fp = flankPoint(state, squad, site.surfaceDir, site.planetIndex, order.side, personLength);
  if (!fp) return false;
  return tacticFrontRear(state, squad).rear.some(p => p.planetIndex === site.planetIndex && arc(p.surfaceDir, fp) < FLANK_ARRIVE * personLength);
}
